import type { QueryKey } from "@tanstack/react-query";

import type { DataResult, DataSchema, data, rest, Surface, tiles } from "@mountaya/client";

/**
 * The generated operation module behind each transport surface.
 *
 * An interface rather than a conditional over `Surface`, so `SurfaceOperations[S]` resolves
 * by lookup and a caller reading the error message sees the operation names rather than a
 * half-evaluated conditional.
 */
interface SurfaceOperations {
  readonly data: typeof data;
  readonly rest: typeof rest;
  readonly tiles: typeof tiles;
}

/**
 * OperationIdOf is every operation a surface has, by name.
 *
 * `bind` is excluded because it is the module's own helper (it applies a transport to the whole
 * namespace) and not an operation anything is cached under.
 *
 * Deriving this from the generated modules rather than from a hand-kept list is what makes it
 * stay true: an operation added to the contract is a key you can write the day it generates,
 * and one removed is a call site that stops compiling rather than a cache entry nothing will
 * ever read again.
 */
export type OperationIdOf<S extends Surface> = Exclude<keyof SurfaceOperations[S], "bind">;

/**
 * OperationArgsOf is the argument object an operation takes, or `never` where it takes none.
 *
 * Every generated operation is `(transport, args, options?)` or `(transport, options?)`, so
 * the question is which of two ARITIES a signature has, and the answer is the middle element
 * of the three-parameter one.
 *
 * `Required` is what makes the count readable: an operation whose arguments are all optional
 * is generated with a default, `args: { … } = {}`, so its second parameter is optional too,
 * and a test for "is the second element required" would answer no for an operation that
 * plainly takes arguments. Stripping optionality first leaves the arity, which is the thing
 * that actually differs.
 */
export type OperationArgsOf<
  S extends Surface,
  Op extends OperationIdOf<S>,
> = SurfaceOperations[S][Extract<Op, keyof SurfaceOperations[S]>] extends (
  ...args: infer P
) => unknown
  ? Required<P> extends readonly [unknown, infer A, unknown]
    ? A
    : never
  : never;

/**
 * OperationSelectionOf is the field selection a Data API operation takes, or `never` on the
 * REST and Tile surfaces, whose answers have one shape.
 *
 * Read off the operation's own `select` option, so a field the operation does not answer is a
 * type error in a key as it is at the call.
 */
export type OperationSelectionOf<S extends Surface, Op extends OperationIdOf<S>> = S extends "data"
  ? SurfaceOperations[S][Extract<Op, keyof SurfaceOperations[S]>] extends (
      ...args: infer P
    ) => unknown
    ? NonNullable<
        NonNullable<P[2]> extends { readonly select?: infer Sel | undefined } ? Sel : never
      >
    : never
  : never;

/**
 * OperationAnswerOf is what an operation resolves to: the body of a REST or Tile operation, and
 * the result of a Data API operation narrowed by its field selection `Sel`.
 *
 * A Data API answer is read off the contract's `Query` type rather than off the operation's
 * signature, because the signature is generic over the selection and a type cannot apply it.
 */
export type OperationAnswerOf<
  S extends Surface,
  Op extends OperationIdOf<S>,
  Sel = undefined,
> = S extends "data"
  ? Op extends keyof DataSchema.Query
    ? DataResult<DataSchema.Query[Op], Sel>
    : never
  : SurfaceOperations[S][Extract<Op, keyof SurfaceOperations[S]>] extends (
        ...args: never[]
      ) => Promise<infer Answer>
    ? Answer
    : never;

/**
 * mountayaKeys builds query keys with a stable shape.
 *
 * Every key starts with `"mountaya"`, so an application can invalidate everything this
 * package caches without knowing what it cached, and keys from the SDK never collide with an
 * application's own.
 *
 * **The credential's scope is the second segment, and it is not optional.** One query cache
 * serves the whole application, so the same resource read under two credentials lands on one
 * entry unless something separates them, and the failure that causes is not a stale render.
 * A share link uses its first read as the token's authorization probe, so an entry seeded by
 * a different credential lets a rotated or foreign token resolve out of cache without ever
 * reaching the API. `MountayaTransport.credentialScope` is the value; `useMountayaKeys`
 * binds it for you, and is what a React caller should reach for.
 *
 * Ordering the segments scope-first is what keeps invalidation useful: `all()` still drops
 * everything, and `scope()` drops exactly one credential's reads, which is what sign-out and a
 * share-token rotation each need.
 *
 * It holds no React, so `@mountaya/client-react/query` publishes it to a Server Component, which
 * then builds the key a client component reads.
 */
export const mountayaKeys = {
  /** Every Mountaya query. `queryClient.invalidateQueries({ queryKey: mountayaKeys.all() })`. */
  all: (): QueryKey => ["mountaya"],
  /**
   * Everything read under one credential. Invalidate this on sign-out, or when a share
   * token rotates, to drop exactly that credential's reads and keep the rest.
   */
  scope: (scope: string): QueryKey => ["mountaya", scope],
  /**
   * One surface under one credential: `"rest"`, `"tiles"` or `"data"`.
   *
   * The TRANSPORT surface, not the client namespace the call was written through. A
   * `studio.itineraries.getItinerary` read travels over `"rest"`, and keying it under
   * `"studio"` would leave it outside the prefix `surface("rest")` invalidates, so the
   * read that was supposed to be dropped keeps being served. `Surface` is the client's own
   * union rather than a string, which is what makes reaching for the namespace a type
   * error instead of a cache that quietly disagrees with itself.
   */
  surface: (scope: string, surface: Surface): QueryKey => ["mountaya", scope, surface],
  /**
   * One operation, optionally narrowed by its arguments.
   *
   * The operation id and the arguments are typed against the surface's own generated module,
   * not `string` and `unknown`. Both mistakes they catch are silent at runtime: a misspelled
   * id writes a key nothing else ever reads, so the cache holds an entry that can never be
   * hit and never be invalidated; and arguments under the wrong field name (`{ id }` where
   * the operation takes `{ itinerary_id }`) write a DIFFERENT key from the one the hook
   * reading that operation writes, so an invalidation aimed at it quietly does nothing.
   *
   * `args` stays optional even where the operation requires them, because omitting them is
   * meaningful here in a way it is not at the call: the shorter key is the PREFIX every
   * narrowed key extends, and it is what "invalidate every read of this operation" names.
   *
   * `select` is a Data API operation's field selection, which shapes its answer, so two
   * selections of one input are two entries. It follows the arguments as a segment of its own,
   * so the unselected key is the prefix of every selection, and it narrows only a key that
   * names its arguments.
   */
  operation: <S extends Surface, Op extends OperationIdOf<S>>(
    scope: string,
    surface: S,
    operationId: Op,
    args?: OperationArgsOf<S, Op>,
    select?: OperationSelectionOf<S, Op>,
  ): QueryKey => {
    if (args === undefined) {
      return ["mountaya", scope, surface, operationId];
    }

    return select === undefined
      ? ["mountaya", scope, surface, operationId, args]
      : ["mountaya", scope, surface, operationId, args, select];
  },
};
